import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { apiCall } from '../api/client';
import ReportEditor from '../components/ReportEditor';
import { StudyFields } from '../components/HeaderView';

interface NavState {
  /** Absolute path to return to when the user hits Back. */
  from?: string;
  /** Human label for the Back button ("Worklist" | "Viewer"). */
  fromLabel?: string;
}

export default function ReportPage() {
  const { studyId: studyIdParam } = useParams<{ studyId: string }>();
  const studyId = Number(studyIdParam);
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state as NavState | null) || {};
  const [study, setStudy] = useState<StudyFields | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Report';
    if (!studyId) return;
    apiCall<StudyFields>('study.get', { studyId }).then((res) => {
      if (res.s === 'ok' && res.data?.[0]) {
        setStudy(res.data[0]);
        document.title = `Report — ${res.data[0].patientName || res.data[0].patientId || studyId}`;
      }
      setLoading(false);
    });
  }, [studyId]);

  function goBack() {
    // Honour the origin from router state so clicking Report from the worklist
    // returns to the worklist, and clicking it from the viewer returns to the viewer.
    if (navState.from) {
      navigate(navState.from);
      return;
    }
    // Direct URL entry (bookmark, refresh) — fall back to viewer if we know the
    // study, otherwise the worklist.
    if (studyId) navigate(`/viewer/${studyId}`);
    else navigate('/worklist');
  }

  if (!studyId) return <div style={{ padding: 20 }}>Invalid study id.</div>;
  // Wait for study data so header-insert has the right patient fields on first render
  if (loading) return <div style={{ padding: 20 }}>Loading study…</div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f0f4f8' }}>
      <ReportEditor
        studyId={studyId}
        modality={study?.modality}
        study={study ?? undefined}
        fullScreen
        backLabel={navState.fromLabel ? `Back to ${navState.fromLabel}` : 'Back to Viewer'}
        onBack={goBack}
      />
    </div>
  );
}
